// CST207 Step 3: replaces `lib/error-messages.ts`.
// ApiError's `userMessage` is a getter that resolves lazily via i18next, so it
// always reflects the *current* language. Previously the message was captured
// at throw time, which would have gone stale after changeLanguage().
import { i18n } from "@/i18n";

const GENERIC_KEY = "errors:UNKNOWN";

/**
 * Look up the localized user-facing message for a given error code. Falls back
 * to the generic UNKNOWN message if the code is not in the `errors` namespace.
 */
export function getUserMessage(code: string): string {
  // Use ":" (i18next namespace separator) not "." (key separator) to reference
  // the `errors` namespace. "errors.X" would look for nested key errors->X in
  // the default (common) namespace, which doesn't exist.
  return i18n.t(`errors:${code}`, { defaultValue: i18n.t(GENERIC_KEY) });
}

export class ApiError extends Error {
  code: string;
  statusCode: number;

  constructor(code: string, detail: string, statusCode: number) {
    super(detail);
    this.name = "ApiError";
    this.code = code;
    this.statusCode = statusCode;
  }

  get userMessage(): string {
    return getUserMessage(this.code);
  }
}

export function parseApiError(err: unknown): { code: string; message: string } {
  if (err instanceof ApiError) {
    return { code: err.code, message: err.userMessage };
  }
  return { code: "UNKNOWN", message: i18n.t(GENERIC_KEY) };
}
